import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyAddressUrl,
  useCollectionBinding,
  usePage,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, EditableCollectionBinding } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';

/** Fallback util-bar surface when primary hasn't hydrated yet. */
const UTIL_BAR_BG_FALLBACK = '#B28C59';

/**
 * Foreground for text/icons on a known surface.
 * Prefer the theme secondary when it clears WCAG AA (4.5:1) against the
 * background; otherwise fall back to white-on-dark / black-on-light via
 * `getContrastTextColor` so every property stays readable.
 */
function pickSecondaryOrContrast(
  background: string,
  secondary: string | null | undefined,
): string {
  if (secondary && contrastRatio(secondary, background) >= 4.5) {
    return secondary;
  }
  return getContrastTextColor(background);
}

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the
 * Interactive Map page — that page renders the ApartmentSync (Repli360 /
 * RRAC) "interactive leasing view" widget, which is keyed by two per-
 * property custom fields: `custom_apartmentsync_javascript_id` (the
 * encoded token in the script URL) and `custom_apartmentsync_config_id`
 * (the site identifier). Both are declared as required-style tokens on
 * the apartment-sync-map block, and a property without both is a
 * half-configured / no-real-map state. If either is empty we drop the
 * page from the nav (the SSG also marks the page hidden + noindex so it
 * isn't crawled — see ssg-entry.ts).
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactUsLinkDef: SlotGroupDefinition = {
  label: 'Contact Us Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time. An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625z"
        clipRule="evenodd"
      />
      <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M12.53 16.28a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 011.06-1.06L12 14.69l6.97-6.97a.75.75 0 111.06 1.06l-7.5 7.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];

  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;

    const key = normalizeDay(entry.day);
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry.day || '').slice(0, 3);
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

function pathsMatch(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  const norm = (p: string) => {
    if (p === '/') return '/';
    return p.endsWith('/') ? p.slice(0, -1) : p;
  };
  return norm(a) === norm(b);
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link, isActive }: { link: NavLink; isActive: boolean }) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        className={`rox-nav-link font-body text-[13px] font-normal tracking-[0.08em] uppercase transition-colors py-2 whitespace-nowrap ${
          isActive ? 'rox-nav-link--active' : ''
        }`}
        aria-current={isActive ? 'page' : undefined}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <a
        href={link.href}
        className={`rox-nav-link font-body text-[13px] font-normal tracking-[0.08em] uppercase transition-colors py-2 flex items-center gap-1 whitespace-nowrap ${
          isActive ? 'rox-nav-link--active' : ''
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-current={isActive ? 'page' : undefined}
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </a>

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {link.children!.map((child) => (
          <a
            key={child.href}
            href={child.href}
            className="rox-nav-link block px-5 py-3 font-body text-[13px] font-normal tracking-[0.08em] uppercase transition-colors"
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({
  navLinks,
  currentPath,
}: {
  navLinks: NavLink[];
  currentPath: string | undefined;
}) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 0;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href}
            className="font-body text-[13px] font-normal tracking-[0.08em] uppercase inline-flex items-center gap-1 pl-[30px]"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-end flex-1 min-w-0"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink
            key={link.href}
            link={link}
            isActive={pathsMatch(currentPath, link.href)}
          />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className="rox-nav-link font-body text-[13px] font-normal tracking-[0.08em] uppercase transition-colors py-2 flex items-center gap-1 whitespace-nowrap pl-[30px]"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{ backgroundColor: 'var(--vibe-color-background)' }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <a
                    href={link.href}
                    className="rox-nav-link block px-5 py-3 font-body text-[13px] font-normal tracking-[0.08em] uppercase transition-colors"
                  >
                    {link.label}
                  </a>
                  {link.children?.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      className="rox-nav-link block pl-9 pr-5 py-3 font-body text-[13px] font-normal tracking-[0.08em] uppercase text-[var(--vibe-color-text-muted)] transition-colors"
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isActive,
}: {
  link: NavLink;
  onNavigate: () => void;
  isActive?: boolean;
}) {
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className={`rox-mobile-nav-link block py-3.5 px-6 font-body text-sm font-normal uppercase tracking-[0.08em] transition-colors border-b border-[var(--vibe-color-border)]/50 ${
        isActive ? 'text-[var(--vibe-color-primary)]' : 'text-[var(--vibe-color-text)]'
      }`}
      aria-current={isActive ? 'page' : undefined}
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE:
//   1. Primary-color utility bar — phone left; Apply Now / Resident Access /
//      Schedule a Tour right (desktop only)
//   2. White main nav — logo left, page links right; soft drop shadow
//   3. Mobile/tablet bar — hamburger | logo | phone (mobile) /
//      Contact Us CTA (tablet)
// Always solid (never transparent over the hero). A spacer reserves the
// fixed header's height so page content starts below it.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  const pageCtx = usePage();
  const currentPath = pageCtx?.path
    ?? (typeof window !== 'undefined' ? window.location.pathname : undefined);

  // Utility-bar text/icon color (phone / CTAs):
  // prefer theme secondary when it clears WCAG AA against the primary
  // util-bar surface; otherwise white-on-dark / black-on-light.
  const tokens = useTokens();
  const secondary = tokens?.color?.secondary;
  const utilBarBg = tokens?.color?.primary || UTIL_BAR_BG_FALLBACK;
  const utilFg = useMemo(
    () => pickSecondaryOrContrast(utilBarBg, secondary),
    [utilBarBg, secondary],
  );
  // Mobile drawer + chrome sit on the page background (typically white) —
  // prefer secondary there when readable, else black/white.
  const drawerAccent = useMemo(
    () =>
      pickSecondaryOrContrast(
        tokens?.color?.background || '#ffffff',
        secondary,
      ),
    [tokens?.color?.background, secondary],
  );

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync IDs. Require BOTH the JavaScript ID and the Config ID.
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;

  // Editor-configurable override. ADDITIVE to ApartmentSync detection.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  const phone = useContentToken('header.phone');
  const officeHoursLabel = useContentToken('header.office_hours_label');
  const contactLabel = useContentToken('header.contact_label');

  const logo = useSlotGroup('header.logo', logoSlotDef);
  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const contactUsLink = useSlotGroup('header.contact_us_link', contactUsLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();

  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // Google Fonts (Lexend Deca used by heading token elsewhere)
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);
  const telHref = `tel:${phone?.replace(/[^+\d]/g, '') || ''}`;

  return (
    <>
      {/* Spacer reserves space for the fixed header so content starts below it */}
      <div aria-hidden="true" style={{ height: headerHeight }} />

      <header
        ref={headerRef}
        className="fixed left-0 right-0 top-0 z-50 w-full bg-[var(--vibe-color-background)]"
        style={{
          ['--rox-util-fg' as string]: utilFg,
          ['--rox-drawer-accent' as string]: drawerAccent,
        }}
      >
        {/* Editor-only control for Interactive Map visibility override */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — desktop only. Phone left;                     */}
          {/* Apply / Resident / Schedule right. Text+icons use             */}
          {/* --rox-util-fg (secondary when readable, else white/black).    */}
          {/* ============================================================ */}
          <div
            className="header-util-row hidden lg:block"
            style={{
              backgroundColor: utilBarBg,
              ['--rox-util-fg' as string]: utilFg,
            }}
          >
            <div className="px-6 xl:px-10">
              <div className="flex items-center justify-between gap-4 min-h-[42px]">
                {/* Left: phone */}
                <div className="flex items-center gap-0 min-w-0 flex-1">
                  <a
                    href={telHref}
                    className="dniphone dniphonehref header-util-phone"
                    aria-label="Call us"
                  >
                    <PhoneIcon className="w-4 h-4 flex-shrink-0" />
                    <EditableToken
                      path="header.phone"
                      defaultField="property_call_tracking_phone"
                      defaultValue="(555) 555-5555"
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>

                {/* Right: utility CTAs */}
                <div className="flex items-center flex-shrink-0">
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      className="header-util-cta"
                    >
                      <DocumentIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{applyNowLink.label || 'Apply Now'}</span>
                    </a>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      className="header-util-cta"
                    >
                      <UserIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{residentAccessLink.label || 'Resident Access'}</span>
                    </a>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                    <a
                      href={scheduleTourLink.url || '/schedule-a-tour'}
                      target={scheduleTourLink._target || '_self'}
                      className="header-util-cta"
                    >
                      <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{scheduleTourLink.label || 'Schedule a Tour'}</span>
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
          </div>

          {/* ============================================================ */}
          {/* Main nav row                                                  */}
          {/* ============================================================ */}
          <div className="header-nav-row">
            <div className="px-4 sm:px-6 xl:px-10">
              {/* Desktop: logo left + nav right */}
              <div className="hidden lg:flex items-center py-[18px]">
                <EditableSlotGroup
                  groupPath="header.logo"
                  definition={logoSlotDef}
                  className="flex items-center flex-shrink-0 mr-6 self-center"
                >
                  <a href="/" className="flex items-center">
                    {logo.image ? (
                      <VibesitesImage
                        url={logo.image}
                        alt={logo.alt || 'Community Logo'}
                        className="block h-10 xl:h-11 w-auto object-contain"
                        sizes="176px"
                      />
                    ) : (
                      <div className="font-heading text-xl xl:text-2xl font-bold text-[var(--vibe-color-primary)]">
                        Roxanne
                      </div>
                    )}
                  </a>
                </EditableSlotGroup>

                <DesktopNav navLinks={navLinks} currentPath={currentPath} />
              </div>

              {/* Mobile/tablet: hamburger | logo | phone (mobile) or Contact Us (tablet) */}
              <div className="lg:hidden relative flex items-center justify-between min-h-16 pt-3 pb-5">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="relative w-10 h-10 flex-shrink-0 flex items-center justify-center text-[var(--rox-drawer-accent,var(--vibe-color-primary))]"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-current transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-current transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-current transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>

                {/* Inset top/bottom match row padding so the logo shares the
                    same vertical center as the hamburger and trailing CTA. */}
                <EditableSlotGroup
                  groupPath="header.logo"
                  definition={logoSlotDef}
                  className="absolute left-1/2 top-3 bottom-5 -translate-x-1/2 flex items-center max-w-[42%] sm:max-w-[48%]"
                >
                  <a href="/" className="flex items-center flex-shrink-0">
                    {logo.image ? (
                      <VibesitesImage
                        url={logo.image}
                        alt={logo.alt || 'Community Logo'}
                        className="block h-8 sm:h-9 w-auto max-h-9 object-contain"
                        sizes="140px"
                      />
                    ) : (
                      <div className="font-heading text-lg font-bold text-[var(--vibe-color-primary)]">
                        Roxanne
                      </div>
                    )}
                  </a>
                </EditableSlotGroup>

                {/* Mobile: click-to-call icon */}
                <a
                  href={telHref}
                  className="dniphone dniphonehref md:hidden w-10 h-10 flex-shrink-0 flex items-center justify-center text-[var(--rox-drawer-accent,var(--vibe-color-primary))]"
                  aria-label="Call us"
                >
                  <PhoneIcon className="w-5 h-5" />
                </a>

                {/* Tablet: Contact Us CTA */}
                <EditableSlotGroup
                  groupPath="header.contact_us_link"
                  definition={contactUsLinkDef}
                  className="hidden md:block flex-shrink-0"
                >
                  <a
                    href={contactUsLink.url || '/contact-us'}
                    target={contactUsLink._target || '_self'}
                    className="header-contact-cta"
                  >
                    {contactUsLink.label || 'Contact Us'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile Menu Overlay                                            */}
        {/* ============================================================== */}
        <div
          className={`mobile-menu-panel lg:hidden fixed inset-x-0 bottom-0 overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `${headerHeight}px`, backgroundColor: 'var(--vibe-color-background)' }}
        >
          <nav role="navigation" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <React.Fragment key={link.href}>
                <MobileNavLink
                  link={link}
                  onNavigate={closeMobileMenu}
                  isActive={pathsMatch(currentPath, link.href)}
                />
                {link.children?.map((child) => (
                  <MobileNavLink
                    key={child.href}
                    link={child}
                    onNavigate={closeMobileMenu}
                    isActive={pathsMatch(currentPath, child.href)}
                  />
                ))}
              </React.Fragment>
            ))}
          </nav>

          {/* Utility CTAs */}
          <div className="px-5 py-5 space-y-2 border-b border-[var(--vibe-color-border)]/50">
            <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
              <a
                href={applyNowLink.url || '/apply-now'}
                target={applyNowLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill flex items-center justify-center gap-2 w-full py-3 text-sm font-normal tracking-[0.1em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                <DocumentIcon className="w-4 h-4" />
                {applyNowLink.label || 'Apply Now'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
              <a
                href={residentAccessLink.url || '/resident-access'}
                target={residentAccessLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill flex items-center justify-center gap-2 w-full py-3 text-sm font-normal tracking-[0.1em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                <UserIcon className="w-4 h-4" />
                {residentAccessLink.label || 'Resident Access'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
              <a
                href={scheduleTourLink.url || '/schedule-a-tour'}
                target={scheduleTourLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill flex items-center justify-center gap-2 w-full py-3 text-sm font-normal tracking-[0.1em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                <CalendarIcon className="w-4 h-4" />
                {scheduleTourLink.label || 'Schedule a Tour'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Office Hours */}
          <EditableCollectionBinding bindingPath="header.office_hours" label="Office Hours">
            <div className="px-6 py-5 border-b border-[var(--vibe-color-border)]/50">
              <EditableToken
                path="header.office_hours_label"
                type="text"
                label="Office Hours Label"
                as="p"
                className="font-body text-base text-[var(--rox-drawer-accent,var(--vibe-color-primary))] mb-3"
              >
                {officeHoursLabel || 'OFFICE HOURS'}
              </EditableToken>
              {groupedHours.length > 0 ? (
                <dl className="space-y-1.5">
                  {groupedHours.map((group, index) => (
                    <div key={index} className="flex justify-between gap-4 font-body text-sm text-[var(--vibe-color-text)]">
                      <dt>{group.shortLabel}</dt>
                      <dd className="text-right">{group.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="font-body text-sm text-[var(--vibe-color-text-muted)]">
                  Office hours will be displayed here.
                </p>
              )}
            </div>
          </EditableCollectionBinding>

          {/* Contact */}
          <div className="px-6 py-5 pb-8">
            <EditableToken
              path="header.contact_label"
              type="text"
              label="Contact Label"
              as="p"
              className="font-body text-base text-[var(--rox-drawer-accent,var(--vibe-color-primary))] mb-3"
            >
              {contactLabel || 'CONTACT'}
            </EditableToken>
            <div className="space-y-3">
              <a
                href={telHref}
                className="dniphone dniphonehref flex items-center gap-2.5 text-sm text-[var(--vibe-color-text)] hover:text-[var(--rox-drawer-accent,var(--vibe-color-primary))] transition-colors"
              >
                <PhoneIcon className="w-4 h-4 text-[var(--rox-drawer-accent,var(--vibe-color-primary))] flex-shrink-0" />
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-[var(--vibe-color-text)] hover:text-[var(--rox-drawer-accent,var(--vibe-color-primary))] transition-colors"
                >
                  <MapPinIcon className="w-4 h-4 text-[var(--rox-drawer-accent,var(--vibe-color-primary))] flex-shrink-0" />
                  <span>{addressLink.address}</span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Roxanne header — primary utility + white nav                    */
        /* ================================================================ */
        /* Utility bar — primary brand surface. Foreground (--rox-util-fg)
           is set inline: theme secondary when it clears WCAG AA against
           this surface, else white/black. */
        .header-util-row {
          background-color: var(--vibe-color-primary);
        }

        .header-util-phone {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          color: var(--rox-util-fg, #ffffff);
          text-decoration: none;
          white-space: nowrap;
          transition: opacity 0.2s ease;
        }
        .header-util-phone:hover {
          opacity: 0.85;
        }

        .header-util-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.5rem 0.85rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 400;
          letter-spacing: 0.06em;
          color: var(--rox-util-fg, #ffffff);
          text-decoration: none;
          white-space: nowrap;
          transition: opacity 0.2s ease;
        }
        .header-util-cta:hover {
          opacity: 0.8;
        }

        .header-nav-row {
          background-color: var(--vibe-color-background);
          box-shadow: 0 3px 11px rgba(0, 0, 0, 0.18);
          position: relative;
          z-index: 1;
        }

        /* Mobile/tablet Contact Us CTA — matches outline CTA style used
           across the theme (mobile util pills, PropertyOverview, etc.). */
        .header-contact-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem 0.75rem;
          font-family: var(--vibe-font-body);
          font-size: 0.6875rem;
          font-weight: 500;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          text-decoration: none;
          white-space: nowrap;
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 2px;
          color: var(--vibe-color-primary);
          background: transparent;
          transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }
        .header-contact-cta:hover {
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        @media (min-width: 640px) {
          .header-contact-cta {
            padding: 0.55rem 0.95rem;
            font-size: 0.75rem;
          }
        }

        /* Desktop nav links — dark body text, secondary on hover, primary when active */
        .rox-nav-link {
          color: #333333;
          padding-left: 30px;
          text-decoration-line: none;
        }
        .rox-nav-link:hover {
          color: var(--vibe-color-secondary);
        }
        .rox-nav-link--active {
          color: var(--vibe-color-primary);
        }

        /* Dropdown panels keep theme text color */
        .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
          padding-left: 1.25rem;
        }
        .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-secondary) !important;
        }

        .mobile-menu-panel .mobile-util-pill:hover {
          color: var(--vibe-color-text-on-primary);
        }
        .mobile-menu-panel .rox-mobile-nav-link:hover {
          color: var(--vibe-color-primary);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.office_hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'OFFICE HOURS',
    },
    'header.contact_label': {
      label: 'Contact Label',
      type: 'text',
      defaultValue: 'CONTACT',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.contact_us_link': contactUsLinkDef,
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
